import {
  ICullOccludedContainersOptions,
  TSideViewSide,
  cullOccludedContainers,
  getSideOfRow,
} from "./cullOccludedContainers";
import {
  IRowPattern,
  SmartPairedBayBlock,
  SmartUnpairedBayBlock,
} from "@tedivo/tedivo-bay-grid-pure";
import { getAllContainersPortSide, getAllContainersStbdSide } from "./getAllContainers";

import { TGetContainersFilterOutput } from "./TGetContainersFilterOutput";
import { TSelectContainerCriteria } from "../useSimpleSideView";
import { getContainersByMaxSizePerStack } from "./getContainersByMaxSizePerStack";
import { getRowContainers } from "./getPerRowContainers";
import { getSideContainers } from "./getSideContainers";

export interface IObtainSideViewFilterFnOptions extends ICullOccludedContainersOptions {
  /**
   * Remove the containers hidden behind nearer rows (see `cullOccludedContainers`) also for the
   * criteria that do not imply it (`all`, `allPortSide`, `allSbtdSide`, `maxSize`, a single row).
   * `portOuter` and `stbdOuter` are always culled: that is their meaning.
   */
  hideOccluded?: boolean;
}

/** The side of the ship a criteria looks from. Even rows are port; everything else is starboard */
export function getSideOfCriteria(
  selectContainerCriteria: TSelectContainerCriteria,
): TSideViewSide {
  switch (selectContainerCriteria) {
    case "portOuter":
    case "allPortSide":
      return "port";
    case "stbdOuter":
    case "all":
    case "allSbtdSide":
    case "maxSize":
      return "starboard";
    default:
      return getSideOfRow(selectContainerCriteria);
  }
}

export function obtainSideViewFilterFn(
  selectContainerCriteria: TSelectContainerCriteria,
  options: IObtainSideViewFilterFnOptions = {},
): TObtainFilterFnOutput {
  const { hideOccluded, ...cullOptions } = options;
  const side = getSideOfCriteria(selectContainerCriteria);

  const withCull = (filterFn: TObtainFilterFnOutput): TObtainFilterFnOutput =>
    hideOccluded
      ? (smartBay) => {
          const result = filterFn(smartBay);
          return {
            ...result,
            containers: cullOccludedContainers(result.containers, side, cullOptions),
          };
        }
      : filterFn;

  switch (selectContainerCriteria) {
    case "maxSize":
      return withCull(getContainersByMaxSizePerStack);
    case "all":
    case "allSbtdSide":
      return withCull(getAllContainersStbdSide);
    case "allPortSide":
      return withCull(getAllContainersPortSide);
    case "portOuter":
      return getSideContainers("port", cullOptions);
    case "stbdOuter":
      return getSideContainers("starboard", cullOptions);
    default:
      return withCull(getRowContainers(selectContainerCriteria as IRowPattern));
  }
}

type TObtainFilterFnOutput = (
  smartBay: SmartUnpairedBayBlock | SmartPairedBayBlock,
) => TGetContainersFilterOutput;
